import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock, Download, Loader2, RefreshCw, Users2, ChevronDown, ChevronRight, Timer } from "lucide-react";
import { Navigate } from "react-router-dom";
import { AppHeader } from "@/components/AppHeader";
import { useAuth } from "@/contexts/AuthContext";
import { isAdmin } from "@/lib/roles";
import api from "@/lib/api";
import { DayDetail, fmtDuration } from "@/components/activity/ActivityParts";

const PERIODS = [
  { id: "today", label: "Heute" },
  { id: "yesterday", label: "Gestern" },
  { id: "7d", label: "7 Tage" },
  { id: "30d", label: "30 Tage" },
];
const ROLE = {
  admin: "Admin", sales_rep: "Vertrieb", sales_manager: "Vertriebsleiter", branch_manager: "Filialleiter",
  sales_director: "Vertriebsdirektor", agent: "Telesales", telesales_director: "Telesales-Direktor",
  installer: "Installateur", backoffice: "Back-Office",
};

function Kpi({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-xl px-3 py-2.5 min-w-0" data-testid={testid}>
      <div className="text-[10.5px] uppercase tracking-wider text-zinc-400 font-bold inline-flex items-center gap-1"><Icon size={11} /> {label}</div>
      <div className="text-[18px] sm:text-[20px] font-bold text-zinc-100 font-mono truncate">{value}</div>
    </div>
  );
}

function UserRow({ u, single, open, onToggle }) {
  return (
    <div className="border-b border-zinc-800 last:border-0" data-testid={`activity-user-${u.id}`}>
      <button type="button" onClick={onToggle} data-testid={`activity-user-toggle-${u.id}`}
              className="w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-zinc-900/60 transition-colors">
        {open ? <ChevronDown size={15} className="text-zinc-500 shrink-0" /> : <ChevronRight size={15} className="text-zinc-500 shrink-0" />}
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-semibold text-zinc-100 truncate">{u.name}</div>
          <div className="text-[11.5px] text-zinc-500 truncate">{ROLE[u.role] || u.role} · {u.email || "—"}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[14px] font-bold font-mono text-[#00E575]" data-testid={`activity-user-total-${u.id}`}>{fmtDuration(u.work_minutes)}</div>
          <div className="text-[11px] text-zinc-500">
            {single ? `${u.first_start}–${u.last_end}` : `${u.days_active} Tag${u.days_active === 1 ? "" : "e"} aktiv`}
          </div>
        </div>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          {u.days.map((d) => <DayDetail key={d.date} day={d} uid={u.id} />)}
        </div>
      )}
    </div>
  );
}

export default function AdminActivity() {
  const { user } = useAuth();
  return isAdmin(user) ? <AdminActivityPage /> : <Navigate to="/dashboard" replace />;
}

function AdminActivityPage() {
  const [period, setPeriod] = useState("today");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/activity", { params: { period } });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aktivität konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await api.get("/admin/activity/export.csv", { params: { period }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `nutzer-aktivitaet-${period}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("CSV-Export fehlgeschlagen");
    } finally {
      setExporting(false);
    }
  };

  const single = period === "today" || period === "yesterday";
  const users = data?.users || [];

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="admin-activity">
      <AppHeader title="Nutzer-Aktivität" back="/dashboard" />
      <main className="max-w-5xl mx-auto px-3 sm:px-5 py-5 sm:py-7">
        <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-zinc-50">Nutzer-Aktivität</h1>
            <p className="text-zinc-400 text-[12.5px] mt-0.5">
              Arbeitszeit je Nutzer · neuer Block nach {data?.gap_minutes ?? 15} min Pause · Daten der letzten 30 Tage
            </p>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={exportCsv} disabled={exporting || !users.length} data-testid="activity-export-csv"
                    className="h-10 px-3 rounded-xl bg-zinc-900 border border-zinc-800 hover:border-[#00E575]/40 text-zinc-200 text-[12.5px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-40">
              {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} CSV
            </button>
            <button type="button" onClick={load} data-testid="activity-refresh" title="Aktualisieren"
                    className="h-10 w-10 rounded-xl bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 flex items-center justify-center">
              <RefreshCw size={14} className={loading ? "animate-spin text-[#00E575]" : "text-zinc-400"} />
            </button>
          </div>
        </div>

        <div className="flex gap-1.5 mb-4 overflow-x-auto" data-testid="activity-periods">
          {PERIODS.map((p) => (
            <button key={p.id} type="button" onClick={() => { setPeriod(p.id); setOpenId(null); }}
                    data-testid={`activity-period-${p.id}`}
                    className={`h-9 px-4 rounded-full text-[13px] font-semibold whitespace-nowrap transition-colors ${
                      period === p.id ? "bg-[#00E575] text-zinc-950" : "bg-zinc-900 border border-zinc-800 text-zinc-300 hover:bg-zinc-800"}`}>
              {p.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-5">
          <Kpi icon={Users2} label="Aktive Nutzer" value={data ? `${data.summary.active_users} / ${data.summary.total_users}` : "…"} testid="activity-kpi-users" />
          <Kpi icon={Clock} label="Arbeitszeit gesamt" value={data ? fmtDuration(data.summary.work_minutes) : "…"} testid="activity-kpi-total" />
          <div className="col-span-2 sm:col-span-1">
            <Kpi icon={Timer} label="Ø je aktivem Nutzer" testid="activity-kpi-avg"
                 value={data ? fmtDuration(data.summary.active_users ? Math.round(data.summary.work_minutes / data.summary.active_users) : 0) : "…"} />
          </div>
        </div>

        <div className="bg-[#18181B] border border-zinc-800 rounded-2xl overflow-hidden" data-testid="activity-user-list">
          {loading && !data ? (
            <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-zinc-400" size={22} /></div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center text-zinc-400 text-sm" data-testid="activity-empty">Im gewählten Zeitraum war niemand aktiv.</div>
          ) : users.map((u) => (
            <UserRow key={u.id} u={u} single={single} open={openId === u.id}
                     onToggle={() => setOpenId(openId === u.id ? null : u.id)} />
          ))}
        </div>
        <p className="text-[11.5px] text-zinc-500 mt-3">
          Gezählt werden nur Minuten mit echter Bedienung der App. Eine offene, unbenutzte App zählt nicht als Arbeitszeit.
        </p>
      </main>
    </div>
  );
}
